import { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { Users, ArrowUp, ArrowDown, ArrowUpDown, Plus } from 'lucide-react';
import {
  Button,
  Datatable,
  DatatableBody,
  DatatableCell,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
  Skeleton,
} from '@monksflow/monks-ui';
import { ToolbarSkeleton } from '@/shared/ui';
import { ContentToolbar, OwnerCell, type OwnerInfo } from '@/shared/ui';
import { EmptyState } from '@/shared/ui/EmptyState';
import { DeleteConfirmationModal } from '@/shared/ui/DeleteConfirmationModal';
import { notificationService } from '@/lib/services/notification.service';
import { useDebounce } from '@/shared/hooks';
import { createComparator } from '@/shared/utils/sorting';
import { usePermissions, useCurrentUserId } from '@/entities/user';
import { ROUTES } from '@/shared/config/routes';
import { readFocusGroupsOrigin, withFocusGroupsOrigin } from '@/shared/utils/focusGroupsNavigation';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDeleteFocusGroup, useFocusGroups } from '../../hooks';
import { buildStatusConfig } from '../../constants';
import { ColumnFilterMenu, type ColumnFilterOption } from '../ColumnFilterMenu';
import { FocusGroupRowActions } from '../FocusGroupRowActions';
import { FocusGroupStatusChip } from '../FocusGroupStatusChip';
import type { FocusGroupOwner, FocusGroupTableData } from '../../types';
import { useTranslation } from 'react-i18next';

type SortDirection = 'asc' | 'desc';

const SKELETON_ROWS = 5;

// Owner names are resolved org-wide by the backend (so org admins / non-members
// resolve too); the frontend only decides whether the owner is the current user.
const toOwnerInfo = (owner: FocusGroupOwner, currentUserId: string | undefined): OwnerInfo => ({
  name: owner.name,
  avatarUrl: owner.avatarUrl,
  isMe: owner.id === currentUserId,
});

// Hoisted to module scope so it keeps a stable identity across renders —
// otherwise React tears down and rebuilds the icon on every parent re-render
// (e.g. each keystroke in the search box, before the debounce fires).
function SortIcon({
  column,
  sortBy,
  sortDirection,
}: {
  column: keyof FocusGroupTableData;
  sortBy: keyof FocusGroupTableData;
  sortDirection: SortDirection;
}) {
  if (sortBy !== column) return <ArrowUpDown className="size-3.5 text-muted-foreground" />;
  return sortDirection === 'asc' ? (
    <ArrowUp className="size-3.5" />
  ) : (
    <ArrowDown className="size-3.5" />
  );
}

export const FocusGroupsContent = () => {
  const { t } = useTranslation('audiences');
  const { t: tCommon } = useTranslation('common');

  const navigate = useNavigate();
  const { state } = useLocation() as { state: unknown };
  // Preserve the section entry origin (e.g. a Tools page) when navigating
  // deeper, so the back button keeps resolving to it.
  const origin = readFocusGroupsOrigin(state);
  const { canAccessFocusGroups } = usePermissions();
  const { data, isPending } = useFocusGroups();
  const currentUserId = useCurrentUserId();
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);
  const [sortBy, setSortBy] = useState<keyof FocusGroupTableData>('title');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [ownerFilter, setOwnerFilter] = useState<string[]>([]);
  const [focusGroupToDelete, setFocusGroupToDelete] = useState<FocusGroupTableData | null>(null);
  const deleteMutation = useDeleteFocusGroup();

  // The actions column is only rendered for managers, so the skeleton and the
  // search-empty row span must follow the same condition to stay aligned.
  const columnCount = canAccessFocusGroups ? 5 : 4;

  const handleEdit = (fg: FocusGroupTableData) => {
    void navigate(`${ROUTES.TOOLS_FOCUS_GROUPS}/${fg.id}`, withFocusGroupsOrigin(origin));
  };

  const handleConfirmDelete = (fg: FocusGroupTableData) => {
    deleteMutation.mutate(fg.id, {
      onSuccess: () => {
        notificationService.showSuccess(t('focusGroups.focusGroupsContent.focusGroupDeleted'));
        setFocusGroupToDelete(null);
      },
      onError: (error: unknown) => {
        const message =
          error instanceof Error
            ? error.message
            : t('focusGroups.focusGroupsContent.couldNotDeleteFocusGroup');
        notificationService.showError(message);
      },
    });
  };

  const handleSort = (property: keyof FocusGroupTableData) => {
    const isAsc = sortBy === property && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const filteredFocusGroups = useMemo((): FocusGroupTableData[] => {
    if (!data?.data) return [];

    let result = data.data;

    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter((fg) => fg.title.toLowerCase().includes(searchLower));
    }

    if (statusFilter.length > 0) {
      result = result.filter((fg) => statusFilter.includes(fg.status));
    }

    if (ownerFilter.length > 0) {
      result = result.filter((fg) => ownerFilter.includes(fg.createdBy));
    }

    return [...result].sort(createComparator(sortBy, sortDirection));
  }, [data, debouncedSearch, statusFilter, ownerFilter, sortBy, sortDirection]);

  const statusOptions = useMemo<ColumnFilterOption[]>(
    () => Object.entries(buildStatusConfig(t)).map(([value, { label }]) => ({ value, label })),
    [t],
  );

  // Owner filter options are the distinct creators present in the data,
  // resolved to display names ("Me" sorted first, then alphabetical).
  const ownerOptions = useMemo<ColumnFilterOption[]>(() => {
    const seen = new Set<string>();
    const options: (ColumnFilterOption & { isMe: boolean })[] = [];
    for (const fg of data?.data ?? []) {
      if (seen.has(fg.createdBy)) continue;
      seen.add(fg.createdBy);
      const isMe = fg.owner.id === currentUserId;
      options.push({
        value: fg.createdBy,
        label: isMe ? tCommon('components.ownerCell.me') : fg.owner.name,
        isMe,
      });
    }
    return options
      .sort((a, b) => {
        if (a.isMe !== b.isMe) return a.isMe ? -1 : 1;
        return a.label.localeCompare(b.label);
      })
      .map(({ value, label }) => ({ value, label }));
  }, [data, currentUserId, tCommon]);

  const handleNewFocusGroup = () => {
    void navigate(ROUTES.TOOLS_FOCUS_GROUPS_NEW, withFocusGroupsOrigin(origin));
  };

  const hasFocusGroups = (data?.data ?? []).length > 0;
  const showInitialEmptyState = !isPending && !hasFocusGroups;
  const showSearchEmptyRow =
    !isPending && hasFocusGroups && filteredFocusGroups.length === 0 && Boolean(debouncedSearch);
  const showToolbar = !isPending && !showInitialEmptyState;

  return (
    <div className="flex h-full flex-col gap-3">
      {isPending && <ToolbarSkeleton showButton={canAccessFocusGroups} />}
      {showToolbar && (
        <ContentToolbar
          searchLabel={t('focusGroups.focusGroupsContent.searchFocusGroups')}
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          createButton={
            canAccessFocusGroups
              ? {
                  label: t('focusGroups.focusGroupsContent.newFocusGroup'),
                  onClick: handleNewFocusGroup,
                  iconPosition: 'start',
                }
              : undefined
          }
        />
      )}
      {showInitialEmptyState ? (
        <EmptyState
          icon={<Users className="size-6" />}
          title={t('focusGroups.focusGroupsContent.startTheConversation')}
          description={t('focusGroups.focusGroupsContent.scheduleALiveInterviewTo')}
        >
          {canAccessFocusGroups && (
            <Button variant="outline" onClick={handleNewFocusGroup}>
              <Plus className="size-4" />
              {t('focusGroups.focusGroupsContent.createFocusGroup')}
            </Button>
          )}
        </EmptyState>
      ) : (
        <>
          <Datatable className="table-fixed **:data-[slot=datatable-cell]:py-2.5 **:data-[slot=datatable-cell]:text-sm **:data-[slot=datatable-cell]:font-normal **:data-[slot=datatable-cell]:tracking-[-0.14px] **:data-[slot=datatable-cell]:text-gray-950 dark:**:data-[slot=datatable-cell]:text-foreground **:data-[slot=datatable-cell]:border-b **:data-[slot=datatable-cell]:border-border **:data-[slot=datatable-head]:text-sm **:data-[slot=datatable-head]:font-normal **:data-[slot=datatable-head]:tracking-[-0.14px] **:data-[slot=datatable-head]:text-muted-foreground px-6">
            <DatatableHeader>
              <DatatableRow>
                <DatatableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort('title')}
                >
                  <div className="flex flex-col items-start gap-2.5 self-stretch">
                    <div className="flex items-center gap-1">
                      {t('focusGroups.focusGroupsContent.name')}
                      <SortIcon column="title" sortBy={sortBy} sortDirection={sortDirection} />
                    </div>
                  </div>
                </DatatableHead>
                <DatatableHead className="w-[160px]">
                  <div className="flex flex-col items-start gap-2.5 self-stretch">
                    {t('focusGroups.focusGroupsContent.totalInterviewed')}
                  </div>
                </DatatableHead>
                <DatatableHead className="w-[140px]">
                  <div className="flex flex-col items-start gap-2.5 self-stretch">
                    <div className="flex items-center gap-1.5">
                      {t('focusGroups.focusGroupsContent.status')}
                      <ColumnFilterMenu
                        label={t('focusGroups.focusGroupsContent.filterByStatus')}
                        options={statusOptions}
                        selected={statusFilter}
                        onChange={setStatusFilter}
                      />
                    </div>
                  </div>
                </DatatableHead>
                <DatatableHead className="w-[200px]">
                  <div className="flex flex-col items-start gap-2.5 self-stretch">
                    <div className="flex items-center gap-1.5">
                      {t('focusGroups.focusGroupsContent.owner')}
                      <ColumnFilterMenu
                        label={t('focusGroups.focusGroupsContent.filterByOwner')}
                        options={ownerOptions}
                        selected={ownerFilter}
                        onChange={setOwnerFilter}
                      />
                    </div>
                  </div>
                </DatatableHead>
                {canAccessFocusGroups && (
                  <DatatableHead className="w-[56px]">
                    <span className="sr-only">{t('focusGroups.focusGroupsContent.actions')}</span>
                  </DatatableHead>
                )}
              </DatatableRow>
            </DatatableHeader>
            <DatatableBody>
              {/* single call site each — inlined instead of `render*` closures. */}
              {isPending &&
                Array.from({ length: SKELETON_ROWS }).map((_, index) => (
                  <DatatableRow key={`skeleton-${index}`}>
                    {Array.from({ length: columnCount }).map((_, colIndex) => (
                      <DatatableCell key={`skeleton-cell-${colIndex}`}>
                        <Skeleton className="h-4 w-3/4" />
                      </DatatableCell>
                    ))}
                  </DatatableRow>
                ))}
              {showSearchEmptyRow && (
                <DatatableRow>
                  <DatatableCell
                    colSpan={columnCount}
                    className="text-center text-muted-foreground py-16"
                  >
                    {t('focusGroups.focusGroupsContent.noResultsFor', { search: debouncedSearch })}
                  </DatatableCell>
                </DatatableRow>
              )}
              {!isPending &&
                filteredFocusGroups.map((focusGroup) => (
                  <DatatableRow
                    key={focusGroup.id}
                    interactive
                    onClick={() =>
                      void navigate(
                        `${ROUTES.TOOLS_FOCUS_GROUPS}/${focusGroup.id}`,
                        withFocusGroupsOrigin(origin),
                      )
                    }
                    className="cursor-pointer"
                  >
                    <DatatableCell>
                      <span className="font-medium">{focusGroup.title}</span>
                    </DatatableCell>
                    <DatatableCell>
                      <div className="flex items-center gap-1">
                        <Users className="size-5 text-gray-950 dark:text-foreground" />
                        <span className="text-xs font-medium">{focusGroup.personaCount}</span>
                      </div>
                    </DatatableCell>
                    <DatatableCell>
                      <FocusGroupStatusChip status={focusGroup.status} />
                    </DatatableCell>
                    <DatatableCell>
                      <OwnerCell owner={toOwnerInfo(focusGroup.owner, currentUserId)} />
                    </DatatableCell>
                    {canAccessFocusGroups && (
                      <DatatableCell>
                        <FocusGroupRowActions
                          focusGroup={focusGroup}
                          onEdit={handleEdit}
                          onDelete={setFocusGroupToDelete}
                        />
                      </DatatableCell>
                    )}
                  </DatatableRow>
                ))}
            </DatatableBody>
          </Datatable>
          <DeleteConfirmationModal
            open={focusGroupToDelete !== null}
            isDeleting={deleteMutation.isPending}
            resourceType={t('focusGroup.focusGroupReport.focusGroup')}
            resourceName={focusGroupToDelete?.title ?? undefined}
            warningMessage={t('focusGroups.focusGroupsContent.thisActionCannotBeUndone')}
            onCancel={() => setFocusGroupToDelete(null)}
            onConfirmDelete={() => {
              if (focusGroupToDelete) {
                handleConfirmDelete(focusGroupToDelete);
              }
            }}
          />
        </>
      )}
    </div>
  );
};
